import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

const REPORT_TYPES = ['Day-wise', 'Week-wise', 'Item-wise', 'GST Report', 'Payment-wise', 'Staff-wise', 'Customer-wise', 'Offer-wise'];

function fmt(n) { return '₹' + (n || 0).toLocaleString('en-IN'); }

export default function Reports() {
  const [type, setType]       = useState('Day-wise');
  const [bills, setBills]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo]   = useState(new Date().toISOString().slice(0, 10));

  const fetchBills = async () => {
    setLoading(true);
    const from = new Date(dateFrom); from.setHours(0, 0, 0, 0);
    const to   = new Date(dateTo);   to.setHours(23, 59, 59, 999);
    const q    = query(collection(db, 'bills'),
      where('createdAt', '>=', Timestamp.fromDate(from)),
      where('createdAt', '<=', Timestamp.fromDate(to)),
      orderBy('createdAt', 'desc'));
    const snap = await getDocs(q);
    setBills(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  useEffect(() => { fetchBills(); }, [dateFrom, dateTo]);

  const totalRevenue  = bills.reduce((a, b) => a + (b.total || 0), 0);
  const totalGst      = bills.reduce((a, b) => a + (b.gst || 0), 0);
  const totalOrders   = bills.length;
  const avgOrder      = totalOrders ? Math.round(totalRevenue / totalOrders) : 0;

  const dayWise = () => {
    const map = {};
    bills.forEach(b => {
      const d = b.createdAt?.toDate?.()?.toLocaleDateString('en-IN') || '—';
      if (!map[d]) map[d] = { date: d, orders: 0, revenue: 0, gst: 0 };
      map[d].orders++;
      map[d].revenue += b.total || 0;
      map[d].gst     += b.gst   || 0;
    });
    return Object.values(map).sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const weekWise = () => {
    const map = {};
    bills.forEach(b => {
      const d   = b.createdAt?.toDate?.();
      if (!d) return;
      const day = d.getDay();
      const mon = new Date(d); mon.setDate(d.getDate() - ((day + 6) % 7));
      const key = mon.toLocaleDateString('en-IN');
      if (!map[key]) map[key] = { week: `Week of ${key}`, orders: 0, revenue: 0, gst: 0 };
      map[key].orders++;
      map[key].revenue += b.total || 0;
      map[key].gst     += b.gst   || 0;
    });
    return Object.values(map);
  };

  const itemWise = () => {
    const map = {};
    bills.forEach(b => {
      (b.items || []).forEach(i => {
        if (!map[i.name]) map[i.name] = { name: i.name, qty: 0, revenue: 0 };
        map[i.name].qty++;
        map[i.name].revenue += i.price || 0;
      });
    });
    return Object.values(map).sort((a, b) => b.revenue - a.revenue);
  };

  const gstReport = () => {
    const map = {};
    bills.forEach(b => {
      const d = b.createdAt?.toDate?.()?.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }) || '—';
      if (!map[d]) map[d] = { month: d, orders: 0, taxable: 0, gst: 0, total: 0 };
      map[d].orders++;
      map[d].taxable += b.subtotal || 0;
      map[d].gst     += b.gst      || 0;
      map[d].total   += b.total    || 0;
    });
    return Object.values(map);
  };

  const paymentWise = () => {
    const map = {};
    bills.forEach(b => {
      const m = b.paymentMode || 'unknown';
      if (!map[m]) map[m] = { mode: m, orders: 0, revenue: 0 };
      map[m].orders++;
      map[m].revenue += b.total || 0;
    });
    return Object.values(map);
  };

  const staffWise = () => {
    const map = {};
    bills.forEach(b => {
      const u = b.createdBy || 'Unknown';
      if (!map[u]) map[u] = { uid: u, orders: 0, revenue: 0 };
      map[u].orders++;
      map[u].revenue += b.total || 0;
    });
    return Object.values(map).sort((a, b) => b.revenue - a.revenue);
  };

  const customerWise = () => {
    const map = {};
    bills.forEach(b => {
      if (!b.customerPhone) return;
      const k = b.customerPhone;
      if (!map[k]) map[k] = { phone: k, name: b.customerName || '—', orders: 0, revenue: 0 };
      map[k].orders++;
      map[k].revenue += b.total || 0;
    });
    return Object.values(map).sort((a, b) => b.revenue - a.revenue);
  };

  const offerWise = () => {
    const map = {};
    bills.forEach(b => {
      const o = b.offer || 'No offer';
      if (!map[o]) map[o] = { offer: o, orders: 0, revenue: 0, discounts: 0 };
      map[o].orders++;
      map[o].revenue   += b.total || 0;
      map[o].discounts += b.offerDiscount || 0;
    });
    return Object.values(map).sort((a, b) => b.orders - a.orders);
  };

  const renderTable = (cols, rows, emptyMsg = 'No data') => (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <table className="table">
        <thead><tr>{cols.map(c => <th key={c}>{c}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={cols.length} style={{ textAlign: 'center', color: '#bbb', padding: 30 }}>{emptyMsg}</td></tr>}
          {rows.map((r, i) => <tr key={i}>{Object.values(r).map((v, j) => <td key={j}>{v}</td>)}</tr>)}
        </tbody>
      </table>
    </div>
  );

  const renderReport = () => {
    if (loading) return <div style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>Loading…</div>;
    switch (type) {
      case 'Day-wise':
        return renderTable(['Date', 'Orders', 'Revenue', 'GST'],
          dayWise().map(r => ({ date: r.date, orders: r.orders, revenue: fmt(r.revenue), gst: fmt(r.gst) })));
      case 'Week-wise':
        return renderTable(['Week', 'Orders', 'Revenue', 'GST'],
          weekWise().map(r => ({ week: r.week, orders: r.orders, revenue: fmt(r.revenue), gst: fmt(r.gst) })));
      case 'Item-wise':
        return renderTable(['Item', 'Qty Sold', 'Revenue'],
          itemWise().map(r => ({ name: r.name, qty: r.qty, revenue: fmt(r.revenue) })));
      case 'GST Report':
        return (
          <div>
            <div style={{ background: '#EAF3DE', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#3B6D11', marginBottom: 12 }}>
              GST Rate: 5% (2.5% CGST + 2.5% SGST) on all food items
            </div>
            {renderTable(['Month', 'Orders', 'Taxable Amount', 'GST Collected', 'Total'],
              gstReport().map(r => ({ month: r.month, orders: r.orders, taxable: fmt(r.taxable), gst: fmt(r.gst), total: fmt(r.total) })))}
          </div>
        );
      case 'Payment-wise':
        return renderTable(['Payment Mode', 'Orders', 'Revenue'],
          paymentWise().map(r => ({ mode: r.mode.toUpperCase(), orders: r.orders, revenue: fmt(r.revenue) })));
      case 'Staff-wise':
        return renderTable(['Staff UID', 'Bills Generated', 'Revenue'],
          staffWise().map(r => ({ uid: r.uid.slice(0, 10) + '…', orders: r.orders, revenue: fmt(r.revenue) })));
      case 'Customer-wise':
        return renderTable(['Phone', 'Name', 'Total Orders', 'Total Spent'],
          customerWise().map(r => ({ phone: r.phone, name: r.name, orders: r.orders, spent: fmt(r.revenue) })));
      case 'Offer-wise':
        return renderTable(['Offer', 'Orders', 'Revenue', 'Discounts Given'],
          offerWise().map(r => ({ offer: r.offer, orders: r.orders, revenue: fmt(r.revenue), disc: fmt(r.discounts) })));
      default: return null;
    }
  };

  return (
    <div>
      <div className="grid4" style={{ marginBottom: 16 }}>
        {[
          ['Total Revenue',  fmt(totalRevenue)],
          ['Total Orders',   totalOrders],
          ['Avg Order Value', fmt(avgOrder)],
          ['GST Collected',  fmt(totalGst)],
        ].map(([l, v]) => (
          <div key={l} className="metric">
            <div className="metric-label">{l}</div>
            <div className="metric-val" style={{ fontSize: 18 }}>{v}</div>
          </div>
        ))}
      </div>

      <div className="card" style={{ marginBottom: 14, padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13, color: '#888', fontWeight: 500 }}>Date range:</div>
          <input type="date" className="input" style={{ width: 150 }} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          <span style={{ color: '#888' }}>to</span>
          <input type="date" className="input" style={{ width: 150 }} value={dateTo} onChange={e => setDateTo(e.target.value)} />
          <button className="btn btn-primary" style={{ fontSize: 13, padding: '7px 16px' }} onClick={fetchBills}>
            Apply
          </button>
          {[
            ['Today',    0,  0],
            ['7 days',   6,  0],
            ['30 days',  29, 0],
            ['This month', new Date().getDate() - 1, 0],
          ].map(([l, back]) => (
            <button key={l} className="btn" style={{ fontSize: 12, padding: '5px 10px' }}
              onClick={() => {
                const f = new Date(); f.setDate(f.getDate() - back);
                setDateFrom(f.toISOString().slice(0, 10));
                setDateTo(new Date().toISOString().slice(0, 10));
              }}>{l}</button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {REPORT_TYPES.map(t => (
          <button key={t} onClick={() => setType(t)} style={{
            padding: '6px 13px', borderRadius: 20, fontSize: 12, cursor: 'pointer', border: '1px solid',
            borderColor: type === t ? '#D85A30' : '#ddd',
            background:  type === t ? '#FAECE7' : '#fff',
            color:       type === t ? '#993C1D' : '#555',
            fontWeight:  type === t ? 600 : 400,
          }}>{t}</button>
        ))}
      </div>

      <div style={{ marginBottom: 6, fontSize: 13, color: '#888' }}>
        Showing <b>{bills.length}</b> bills from {dateFrom} to {dateTo}
      </div>
      {renderReport()}
    </div>
  );
}